import { Firestore } from 'firebase/firestore';

import { FirebaseContentVersioning } from './content-versioning';
import { synchronizePublishedNovel } from './publication-sync';

import type { ContentStore } from '../contracts/content';
import type { ContentDiff, ContentSnapshot } from '@/lib/text-versioning';
import type { Novel, ContentDocument } from '@/lib/types';
import type { Functions } from 'firebase/functions';

import { ContentDao } from '@/api/daos/content-dao';


export class FirebaseContentStore implements ContentStore {
  private versioning: FirebaseContentVersioning;

  constructor(private firestore: Firestore, private functions: Functions) {
    this.versioning = new FirebaseContentVersioning(firestore);
  }

  async getContent(
    ownerId: string,
    novel: Novel,
    contentId: string
  ): Promise<ContentDocument | null> {
    const contentDao = new ContentDao(this.firestore, (ownerId, novelId) => synchronizePublishedNovel(this.functions, ownerId, novelId));
    return contentDao.getContent<ContentDocument>(ownerId, novel, contentId);
  }

  subscribeContent(
    ownerId: string,
    novel: Novel,
    contentId: string,
    onUpdate: (doc: ContentDocument | null) => void,
    onError: (err: Error) => void
  ): () => void {
    const contentDao = new ContentDao(this.firestore, (ownerId, novelId) => synchronizePublishedNovel(this.functions, ownerId, novelId));
    return contentDao.subscribeContent<ContentDocument>(ownerId, novel, contentId, onUpdate, onError);
  }

  async updateContent(
    ownerId: string,
    novel: Novel,
    contentId: string,
    updates: Partial<ContentDocument>
  ): Promise<void> {
    const contentDao = new ContentDao(this.firestore, (ownerId, novelId) => synchronizePublishedNovel(this.functions, ownerId, novelId));
    await contentDao.updateContent(ownerId, novel, contentId, updates);
  }

  async createContent(
    ownerId: string,
    novel: Novel,
    content: ContentDocument
  ): Promise<void> {
    const contentDao = new ContentDao(this.firestore, (ownerId, novelId) => synchronizePublishedNovel(this.functions, ownerId, novelId));
    await contentDao.createContent(ownerId, novel, content);
  }

  async deleteContent(
    ownerId: string,
    novel: Novel,
    contentId: string
  ): Promise<void> {
    const contentDao = new ContentDao(this.firestore, (ownerId, novelId) => synchronizePublishedNovel(this.functions, ownerId, novelId));
    await contentDao.deleteContent(ownerId, novel, contentId);
  }

  async getOrCreateContent(
    ownerId: string,
    novel: Novel,
    contentId: string,
    chapterId: string | null,
    initialContent?: string
  ): Promise<ContentDocument> {
    const content = await this.versioning.getOrCreateContent(ownerId, novel, contentId, chapterId, initialContent);
    await synchronizePublishedNovel(this.functions, ownerId, novel.id);
    return content;
  }

  async saveContentVersion(
    ownerId: string,
    novel: Novel,
    contentId: string,
    newContent: string,
    isAutomatic: boolean,
    expectedVersion?: number,
    skipHistory?: boolean
  ): Promise<boolean> {
    const saved = await this.versioning.saveContentVersion(ownerId, novel, contentId, newContent, isAutomatic, expectedVersion, skipHistory);
    if (saved) await synchronizePublishedNovel(this.functions, ownerId, novel.id);
    return saved;
  }

  async getDiffsForDay(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string,
    limitCount?: number
  ): Promise<ContentDiff[]> {
    return this.versioning.getDiffsForDay(ownerId, novelId, contentId, dayKey, limitCount);
  }

  async getRecentHistory(
    ownerId: string,
    novelId: string,
    contentId: string,
    limitCount?: number
  ): Promise<ContentDiff[]> {
    return this.versioning.getRecentHistory(ownerId, novelId, contentId, limitCount);
  }

  async getSnapshotForDay(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string
  ): Promise<ContentSnapshot | null> {
    return this.versioning.getSnapshotForDay(ownerId, novelId, contentId, dayKey);
  }

  async reconstructTextAtPoint(
    ownerId: string,
    novelId: string,
    contentId: string,
    dayKey: string,
    seq: number
  ): Promise<{ content: string; previousContent: string; success: boolean }> {
    return this.versioning.reconstructTextAtPoint(ownerId, novelId, contentId, dayKey, seq);
  }
}
